/**
 * coordinate接口配置
 */
import type { ApiEndpoint } from '../types';
import { API_CATEGORIES } from '../constants';

export const coordinateEndpoints: ApiEndpoint[] = [
  {
    id: 'coordinate-create',
    method: 'POST',
    path: '/api/coordinates',
    name: '创建坐标',
    description: '为空间添加坐标信息',
    category: API_CATEGORIES.COORDINATE,
    bodyParams: [
      { name: 'spaceId', type: 'string', required: true, description: '空间ID（UUID）' },
      { name: 'coordinateType', type: 'string', required: true, description: '坐标类型', example: 'TWO_D|THREE_D' },
      { name: 'coordinateSystem', type: 'string', required: true, description: '坐标系名称（1-100字符）' },
      { name: 'origin', type: 'object', required: false, description: '原点坐标（2D: {x, y}, 3D: {x, y, z}）' },
      { name: 'boundary', type: 'array|object', required: true, description: '边界（2D: [[x,y],...], 3D: {min: {x,y,z}, max: {x,y,z}}）' },
      { name: 'center', type: 'object', required: true, description: '中心点坐标（2D: {x, y}, 3D: {x, y, z}）' },
      { name: 'isDefault', type: 'boolean', required: false, description: '是否默认坐标', example: 'false' }
    ],
    responseFields: [
      { name: 'success', type: 'boolean', description: '请求是否成功' },
      { name: 'data', type: 'object', description: '创建的坐标信息' },
      { name: 'message', type: 'string', description: '响应消息' }
    ]
  },
  {
    id: 'coordinate-space',
    method: 'GET',
    path: '/api/coordinates/space/:spaceId',
    name: '获取空间坐标',
    description: '获取指定空间的所有坐标',
    category: API_CATEGORIES.COORDINATE,
    pathParams: [
      { name: 'spaceId', type: 'string', required: true, description: '空间ID（UUID）' }
    ],
    responseFields: [
      { name: 'success', type: 'boolean', description: '请求是否成功' },
      { name: 'data', type: 'array', description: '坐标列表' }
    ],
    responseExample: JSON.stringify({
      success: true,
      data: [
        {
          id: "coord-id-1",
          spaceId: "55a8b310-f590-4671-8a78-db5a9de76e3c",
          coordinateType: "TWO_D",
          coordinateSystem: "WGS84",
          origin: { x: 0, y: 0 },
          boundary: [[0, 0], [100, 0], [100, 100], [0, 100]],
          center: { x: 50, y: 50 },
          isDefault: true,
          createdAt: "2025-10-07T11:46:16.822Z",
          updatedAt: "2025-10-07T11:46:16.822Z"
        }
      ],
      message: "查询成功"
    }, null, 2)
  },
  {
    id: 'coordinate-default',
    method: 'GET',
    path: '/api/coordinates/space/:spaceId/default',
    name: '获取空间默认坐标',
    description: '获取指定空间的默认坐标',
    category: API_CATEGORIES.COORDINATE,
    pathParams: [
      { name: 'spaceId', type: 'string', required: true, description: '空间ID（UUID）' }
    ],
    responseFields: [
      { name: 'success', type: 'boolean', description: '请求是否成功' },
      { name: 'data', type: 'object', description: '默认坐标信息' }
    ]
  },
  {
    id: 'coordinate-update',
    method: 'PUT',
    path: '/api/coordinates/:id',
    name: '更新坐标',
    description: '更新坐标信息',
    category: API_CATEGORIES.COORDINATE,
    pathParams: [
      { name: 'id', type: 'string', required: true, description: '坐标ID（UUID）' }
    ],
    bodyParams: [
      { name: 'coordinateType', type: 'string', required: false, description: '坐标类型' },
      { name: 'coordinateSystem', type: 'string', required: false, description: '坐标系名称' },
      { name: 'origin', type: 'object', required: false, description: '原点坐标' },
      { name: 'boundary', type: 'array|object', required: false, description: '边界' },
      { name: 'center', type: 'object', required: false, description: '中心点坐标' },
      { name: 'isDefault', type: 'boolean', required: false, description: '是否默认坐标' }
    ],
    responseFields: [
      { name: 'success', type: 'boolean', description: '请求是否成功' },
      { name: 'data', type: 'object', description: '更新后的坐标信息' },
      { name: 'message', type: 'string', description: '响应消息' }
    ]
  },
  {
    id: 'coordinate-delete',
    method: 'DELETE',
    path: '/api/coordinates/:id',
    name: '删除坐标',
    description: '删除坐标信息',
    category: API_CATEGORIES.COORDINATE,
    pathParams: [
      { name: 'id', type: 'string', required: true, description: '坐标ID（UUID）' }
    ],
    responseFields: [
      { name: 'success', type: 'boolean', description: '请求是否成功' },
      { name: 'message', type: 'string', description: '响应消息' }
    ],
    requiredPermission: 'msspace:coordinate:view'
  },
  {
    id: 'coordinate-check-point',
    method: 'POST',
    path: '/api/coordinates/space/:spaceId/check-point',
    name: '检查点是否在空间内',
    description: '检查指定点是否在空间范围内',
    category: API_CATEGORIES.COORDINATE,
    pathParams: [
      { name: 'spaceId', type: 'string', required: true, description: '空间ID（UUID）' }
    ],
    bodyParams: [
      { name: 'point', type: 'object', required: true, description: '点坐标（2D: {x, y}, 3D: {x, y, z}）' }
    ],
    responseFields: [
      { name: 'success', type: 'boolean', description: '请求是否成功' },
      { name: 'data', type: 'object', description: '检查结果' }
    ]
  },
  {
    id: 'coordinate-distance',
    method: 'GET',
    path: '/api/coordinates/distance/:spaceId1/:spaceId2',
    name: '计算空间距离',
    description: '计算两个空间之间的距离',
    category: API_CATEGORIES.COORDINATE,
    pathParams: [
      { name: 'spaceId1', type: 'string', required: true, description: '空间1 ID（UUID）' },
      { name: 'spaceId2', type: 'string', required: true, description: '空间2 ID（UUID）' }
    ],
    responseFields: [
      { name: 'success', type: 'boolean', description: '请求是否成功' },
      { name: 'data', type: 'object', description: '距离信息' }
    ]
  },
  {
    id: 'coordinate-search-range',
    method: 'POST',
    path: '/api/coordinates/search/range',
    name: '查找范围内空间',
    description: '查找指定范围内的空间',
    category: API_CATEGORIES.COORDINATE,
    bodyParams: [
      { name: 'range', type: 'object', required: true, description: '范围定义' },
      { name: 'coordinateSystem', type: 'string', required: false, description: '坐标系名称' }
    ],
    responseFields: [
      { name: 'success', type: 'boolean', description: '请求是否成功' },
      { name: 'data', type: 'array', description: '范围内的空间列表' }
    ]
  },
];
